import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useAuth } from '../contexts/AuthContext';
import { uploadImageToCloudflare, uploadVideoToCloudflare } from '../utils/cloudflareUpload';
import './GetFeatured.css';

// ── Crop helpers ──────────────────────────────────────────────────────────────
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}
// ─────────────────────────────────────────────────────────────────────────────

const GetFeatured = () => {
  const { submitNomination, loading } = useAuth();
  const [nominationType, setNominationType] = useState('myself');
  const isSomeoneElse = nominationType === 'someone_else';
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    primarySport: '',
    secondarySports: '',
    city: '',
    state: '',
    reason: '',
    email: '',
    phone: '',
    nominatorName: '',
    notifyMe: false
  });

  // Photo state (cropped blobs + previews)
  const [photoBlobs, setPhotoBlobs] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Video state
  const [videoFile, setVideoFile] = useState(null);
  const [videoName, setVideoName] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  // Open crop modal when user picks a file
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (photoBlobs.length >= 3) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    if (formErrors.photo) setFormErrors(prev => { const n = { ...prev }; delete n.photo; return n; });
  };

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!rawImageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
    const previewUrl = URL.createObjectURL(blob);
    setPhotoBlobs(prev => [...prev, blob]);
    setPhotoPreviews(prev => [...prev, previewUrl]);
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const handleRemovePhoto = (index) => {
    setPhotoBlobs(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoFile(file);
    setVideoName(file.name);
    setVideoPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;

    if (isSomeoneElse && !formData.nominatorName.trim()) errors.nominatorName = 'Your name is required';
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.primarySport) errors.primarySport = 'Primary sport is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.reason.trim()) errors.reason = 'Please tell us why they should be featured';
    if (formData.reason.trim().length < 20) errors.reason = 'Please provide more details (at least 20 characters)';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (formData.phone.trim() && !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validateForm()) return;

    try {
      setUploading(true);

      // Upload cropped photos to Cloudflare
      const photoUrls = [];
      for (const blob of photoBlobs) {
        const file = new File([blob], 'athlete-photo.jpg', { type: 'image/jpeg' });
        const url = await uploadImageToCloudflare(file);
        photoUrls.push(url);
      }

      const videoResult = videoFile ? await uploadVideoToCloudflare(videoFile, setVideoProgress) : null;

      const result = await submitNomination({
        ...formData,
        athletePhotos: photoUrls,
        videoUid: videoResult?.uid ?? null,
      });

      if (result.success) {
        setShowModal(true);
        setFormData({
          firstName: '', lastName: '', primarySport: '', secondarySports: '',
          city: '', state: '', reason: '', email: '', phone: '',
          nominatorName: '', notifyMe: false
        });
        setPhotoBlobs([]);
        setPhotoPreviews([]);
        setVideoFile(null);
        setVideoName(null);
        setVideoPreview(null);
        setVideoProgress(0);
        setFormErrors({});
        setSubmitError(null);
      } else {
        setSubmitError(result.error || 'Failed to submit nomination. Please try again.');
      }
    } catch (err) {
      setSubmitError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="get-featured-wrapper">
      <div className="container-fluid get-featured-container">
        <div className="row">
          <div className="col-12">
            <div className="get-featured-header">
              <h1 className="get-featured-title">Nominate an Athlete</h1>
              <p className="get-featured-subtitle">
                Nominate a youth athlete to be considered for a featured spot on LegacyStat.
              </p>
            </div>

            <form className="get-featured-form" onSubmit={handleSubmit} noValidate>

              {/* Who are you nominating? */}
              <div className="gf-nomtype-row">
                <p className="gf-nomtype-label">Who are you nominating?</p>
                <div className="gf-nomtype-toggle">
                  <button type="button" className={`gf-nomtype-btn ${!isSomeoneElse ? 'active' : ''}`} onClick={() => setNominationType('myself')}>
                    <img src="/assets/images/athlete-icon.png" alt="" className="gf-nomtype-icon" />
                    <span className="gf-nomtype-text">
                      <span className="gf-nomtype-title">Myself</span>
                      <span className="gf-nomtype-sub">I want to be featured</span>
                    </span>
                  </button>
                  <button type="button" className={`gf-nomtype-btn ${isSomeoneElse ? 'active' : ''}`} onClick={() => setNominationType('someone_else')}>
                    <img src="/assets/images/athlete-icon.png" alt="" className="gf-nomtype-icon" />
                    <span className="gf-nomtype-text">
                      <span className="gf-nomtype-title">Someone else</span>
                      <span className="gf-nomtype-sub">I'm nominating someone else</span>
                    </span>
                  </button>
                </div>
              </div>

              <div className="gf-section-heading">
                <h3 className="gf-section-title">{isSomeoneElse ? 'Athlete Info' : 'Your Info'}</h3>
                <p className="gf-section-sub">{isSomeoneElse ? 'Tell us about the athlete you are nominating.' : 'Tell us about yourself.'}</p>
              </div>

              {/* Name */}
              <div className="row get-featured-form-row">
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">First Name *</label>
                    <input type="text" name="firstName" className={`get-featured-input ${formErrors.firstName ? 'error' : ''}`} placeholder={isSomeoneElse ? "Enter athlete's first name" : "Enter your first name"} value={formData.firstName} onChange={handleChange} />
                    {formErrors.firstName && <div className="get-featured-error">{formErrors.firstName}</div>}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">Last Name *</label>
                    <input type="text" name="lastName" className={`get-featured-input ${formErrors.lastName ? 'error' : ''}`} placeholder={isSomeoneElse ? "Enter athlete's last name" : "Enter your last name"} value={formData.lastName} onChange={handleChange} />
                    {formErrors.lastName && <div className="get-featured-error">{formErrors.lastName}</div>}
                  </div>
                </div>
              </div>

              {/* Sport */}
              <div className="row get-featured-form-row">
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">Primary Sport *</label>
                    <select name="primarySport" className={`get-featured-select ${formErrors.primarySport ? 'error' : ''}`} value={formData.primarySport} onChange={handleChange}>
                      <option value="">{isSomeoneElse ? "Select athlete's primary sport" : "Select your primary sport"}</option>
                      {['Flag Football','Football','Basketball','Soccer','Baseball','Softball','Volleyball','Track & Field','Wrestling','Boxing','Martial Arts','Tennis','Golf','Swimming','Cheer / Dance','Other'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {formErrors.primarySport && <div className="get-featured-error">{formErrors.primarySport}</div>}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">Secondary Sports (optional)</label>
                    <select name="secondarySports" className="get-featured-select" value={formData.secondarySports} onChange={handleChange}>
                      <option value="">Select secondary sports</option>
                      {['Flag Football','Football','Basketball','Soccer','Baseball','Softball','Volleyball','Track & Field','Wrestling','Boxing','Martial Arts','Tennis','Golf','Swimming','Cheer / Dance','Other'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="row get-featured-form-row">
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">City *</label>
                    <input type="text" name="city" className={`get-featured-input ${formErrors.city ? 'error' : ''}`} placeholder={isSomeoneElse ? "Enter athlete's city" : "Enter your city"} value={formData.city} onChange={handleChange} />
                    {formErrors.city && <div className="get-featured-error">{formErrors.city}</div>}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">State *</label>
                    <input type="text" name="state" className={`get-featured-input ${formErrors.state ? 'error' : ''}`} placeholder={isSomeoneElse ? "Enter athlete's state" : "Enter your state"} value={formData.state} onChange={handleChange} />
                    {formErrors.state && <div className="get-featured-error">{formErrors.state}</div>}
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="row get-featured-form-row">
                <div className="col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">{isSomeoneElse ? 'Why should they be featured? *' : 'Why should you be featured? *'}</label>
                    <textarea name="reason" className={`get-featured-textarea ${formErrors.reason ? 'error' : ''}`} placeholder={isSomeoneElse ? "Share their achievements, character, leadership, effort, or impact." : "Share your achievements, character, leadership, effort, or impact."} value={formData.reason} onChange={handleChange} rows="6" />
                    {formErrors.reason && <div className="get-featured-error">{formErrors.reason}</div>}
                    <div className="get-featured-character-count">{formData.reason.length} characters (minimum 20)</div>
                  </div>
                </div>
              </div>

              {/* ── Photo Upload with 9:16 Cropper ── */}
              <div className="gf-video-section">
                <div className="gf-section-heading">
                  <h3 className="gf-section-title">
                    <svg className="gf-section-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="#D8A134" strokeWidth="1.8"/>
                    </svg>
                    Athlete Photos (Optional · Max 3)
                  </h3>
                  <p className="gf-section-sub">Upload up to 3 photos. Each photo will be cropped to 9:16 for the best spotlight card result.</p>
                </div>

                <div className="gf-photos-container">
                  {photoPreviews.map((preview, index) => (
                    <div key={index} className="gf-photo-preview-item">
                      <img src={preview} alt={`Preview ${index + 1}`} className="gf-photo-preview" />
                      <button type="button" className="gf-photo-remove-btn" onClick={() => handleRemovePhoto(index)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6L18 18"/>
                        </svg>
                      </button>
                    </div>
                  ))}

                  {photoBlobs.length < 3 && (
                    <label className="gf-photo-add-btn">
                      <div className="gf-photo-add-content">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D8A134" strokeWidth="2">
                          <path d="M12 5v14M5 12h14"/>
                        </svg>
                        <span>Add Photo ({photoBlobs.length}/3)</span>
                      </div>
                      <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePhotoChange} />
                    </label>
                  )}
                </div>

                {/* Video Upload */}
                <div className="gf-section-heading" style={{ marginTop: '28px' }}>
                  <h3 className="gf-section-title">
                    <svg className="gf-section-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="1" y="5" width="14" height="14" rx="2" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 9.5L23 5V19L15 14.5" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Highlight Video (Optional)
                  </h3>
                  <p className="gf-section-sub">{isSomeoneElse ? 'Show us what makes them stand out.' : 'Show us what makes you stand out.'}</p>
                </div>

                {videoPreview ? (
                  <div className="gf-video-preview-wrap">
                    <video src={videoPreview} controls className="gf-video-preview">Your browser does not support the video tag.</video>
                    <div className="gf-video-file-info">
                      <p className="gf-video-file-name">{videoName}</p>
                      {uploading && videoProgress > 0 && <p className="gf-video-upload-sub" style={{ color: '#D8A134' }}>Uploading... {videoProgress}%</p>}
                    </div>
                    <label className="gf-video-choose-btn">Change Video<input type="file" accept="video/*" style={{ display: 'none' }} onChange={handleVideoChange} /></label>
                  </div>
                ) : (
                  <div className="gf-video-dropzone">
                    <div className="gf-video-inner-row">
                      <div className="gf-video-cloud-icon">
                        <svg width="40" height="34" viewBox="0 0 48 40" fill="none">
                          <path d="M24 28V12M24 12L18 18M24 12L30 18" stroke="#D8A134" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 30C4.686 30 2 27.314 2 24C2 20.686 4.686 18 8 18C8.34 18 8.674 18.028 9 18.08C9.994 14.566 13.21 12 17 12C18.88 12 20.606 12.648 21.97 13.726C22.938 10.474 25.944 8 29.5 8C33.918 8 37.5 11.582 37.5 16C37.5 16.136 37.496 16.27 37.49 16.404C37.66 16.36 37.828 16.316 38 16.316C41.314 16.316 44 19.002 44 22.316C44 25.63 41.314 28 38 28" stroke="#D8A134" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="gf-video-text-col">
                        <p className="gf-video-upload-title">Upload Video</p>
                        <p className="gf-video-upload-sub">MP4, MOV &bull; Up to 200MB</p>
                      </div>
                    </div>
                    <label className="gf-video-choose-btn">Choose Video<input type="file" accept="video/*" style={{ display: 'none' }} onChange={handleVideoChange} /></label>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div className="gf-section-heading">
                <h3 className="gf-section-title">{isSomeoneElse ? 'Your Info (Nominator)' : 'Your Contact Info'}</h3>
                <p className="gf-section-sub">{isSomeoneElse ? "We'll only use this to notify you if they are selected." : "We'll only use this to notify you if you are selected."}</p>
              </div>

              {isSomeoneElse && (
                <div className="row get-featured-form-row">
                  <div className="col-12 get-featured-form-col">
                    <div className="get-featured-form-group">
                      <label className="get-featured-label">Your Name *</label>
                      <input type="text" name="nominatorName" className={`get-featured-input ${formErrors.nominatorName ? 'error' : ''}`} placeholder="Enter your full name" value={formData.nominatorName} onChange={handleChange} />
                      {formErrors.nominatorName && <div className="get-featured-error">{formErrors.nominatorName}</div>}
                    </div>
                  </div>
                </div>
              )}

              <div className="row get-featured-form-row">
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">Email Address *</label>
                    <input type="email" name="email" className={`get-featured-input ${formErrors.email ? 'error' : ''}`} placeholder="Write your email here" value={formData.email} onChange={handleChange} />
                    {formErrors.email && <div className="get-featured-error">{formErrors.email}</div>}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12 get-featured-form-col">
                  <div className="get-featured-form-group">
                    <label className="get-featured-label">Phone (optional)</label>
                    <input type="tel" name="phone" className={`get-featured-input ${formErrors.phone ? 'error' : ''}`} placeholder="Enter your phone number" value={formData.phone} onChange={handleChange} />
                    {formErrors.phone && <div className="get-featured-error">{formErrors.phone}</div>}
                  </div>
                </div>
              </div>

              <div className="row get-featured-form-row">
                <div className="col-12 get-featured-form-col">
                  <div className="get-featured-checkbox-wrapper">
                    <label className="get-featured-checkbox-label">
                      <input type="checkbox" name="notifyMe" className="get-featured-checkbox-input" checked={formData.notifyMe} onChange={handleChange} />
                      <span className="get-featured-checkbox-custom"></span>
                      <span className="get-featured-checkbox-text">Notify me about new featured athletes and LegacyStat updates.</span>
                    </label>
                    <p className="get-featured-checkbox-helper">LegacyStat is a new youth-sports platform launching soon — get early access updates, featured athlete alerts, and important announcements.</p>
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="get-featured-submit-error" style={{ color: 'red', textAlign: 'center', marginBottom: '16px' }}>{submitError}</div>
              )}

              <div className="get-featured-button-wrapper">
                <button type="submit" className="get-featured-submit-btn" disabled={loading || uploading}>
                  <span className="get-featured-submit-btn-full">{uploading ? 'Uploading media...' : loading ? 'Submitting...' : 'Submit an Athlete'}</span>
                  <span className="get-featured-submit-btn-short">{uploading || loading ? '...' : 'Submit'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── 9:16 Crop Modal ── */}
      {cropModalOpen && rawImageSrc && (
        <div className="gf-crop-overlay">
          <div className="gf-crop-modal">
            <div className="gf-crop-header">
              <h3 className="gf-crop-title">Crop Photo</h3>
              <p className="gf-crop-sub">Drag and pinch to frame the perfect shot (3:4)</p>
            </div>
            <div className="gf-crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={3 / 4}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="gf-crop-zoom-row">
              <span className="gf-crop-zoom-label">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="gf-crop-zoom-slider"
              />
            </div>
            <div className="gf-crop-actions">
              <button type="button" className="gf-crop-cancel" onClick={handleCropCancel}>Cancel</button>
              <button type="button" className="gf-crop-confirm" onClick={handleCropConfirm}>Use This Crop</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showModal && (
        <div className="get-featured-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="get-featured-modal" onClick={(e) => e.stopPropagation()}>
            <button className="get-featured-modal-close get-featured-modal-close-desktop" onClick={() => setShowModal(false)}>
              <img src="/assets/images/close-modal-icon.png" alt="Close" />
            </button>
            <div className="get-featured-modal-header-mobile">
              <h3 className="get-featured-modal-title-mobile">Submitted</h3>
              <button className="get-featured-modal-close get-featured-modal-close-mobile" onClick={() => setShowModal(false)}>
                <img src="/assets/images/close-modal-icon.png" alt="Close" />
              </button>
            </div>
            <div className="get-featured-modal-divider-mobile"></div>
            <div className="get-featured-modal-icon">
              <img src="/assets/images/submission-icon.png" alt="Success" />
            </div>
            <h2 className="get-featured-modal-title">Thank you for Submission</h2>
            <div className="get-featured-modal-text">
              <p>Thank you for submission</p>
              <p>Your Athlete has been nominated for LegacyStat Spotlight consideration.</p>
              <p>If selected, we will reach out to you more information.</p>
            </div>
            <button className="get-featured-modal-button" onClick={() => setShowModal(false)}>Ok</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetFeatured;
